import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ScanLine, ShieldCheck, Layers, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, track } from "@/lib/api";

const TYPES = [
  { icon: ShieldCheck, tag: "Coatings", title: "Surface Protection Coatings", desc: "For suitable stone, glass, fabric, leather, wood, metal and other compatible surfaces — bonded protection that preserves the natural finish." },
  { icon: Layers, tag: "SPF", title: "SPF — Surface Protection Film", desc: "For suitable high-contact hard surfaces where a physical, near-invisible film is the right protection — tables, countertops and more." },
  { icon: Sparkles, tag: "Specialty", title: "Specialty Protection", desc: "Purpose-built systems such as Velvet Guard for delicate fabrics and Crystal Shield for glass, matched to the surface." },
];

export default function SurfaceExplorer() {
  const [families, setFamilies] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    track("page_view", { page: "protection" });
    api.get("/surface-guide").then(({ data }) => setFamilies(data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#080808]" data-testid="surface-explorer-page">
      <Seo title="Protection by Surface" description="Explore ERRAH surface protection for stone, wood, fabric, velvet, leather, glass, kitchen, bathroom, metal and more. Every surface. One ERRAH." />

      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 pt-28 pb-10">
        <Reveal>
          <p className="errah-label gold-text mb-4">Protection by surface</p>
          <h1 className="serif-head text-4xl sm:text-5xl lg:text-7xl max-w-4xl">EVERY SURFACE.<br /><span className="gold-text italic">ONE ERRAH.</span></h1>
          <p className="mt-6 text-gray-400 max-w-2xl leading-relaxed">
            From marble and wood to velvet, leather, glass and fabric — discover the right protection for every surface. Not sure what you have? Let ERRAH AI identify it for you.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => navigate("/ai-scan")} data-testid="explorer-scan-cta"
              className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
              <ScanLine size={16} /> Scan Your Surface
            </button>
            <Link to="/pricing" data-testid="explorer-pricing-cta"
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
              View Pricing
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 pb-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5" data-testid="surface-grid">
          {families.map((f, i) => (
            <Reveal key={f.slug} delay={(i % 5) * 0.05}>
              <Link to={`/protection/${f.slug}`} data-testid={`surface-card-${f.slug}`}
                className="group block hover-zoom relative aspect-[3/4] overflow-hidden hair-border">
                <img src={f.image} alt={f.title} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-lg leading-tight">{f.title}</h3>
                    <ArrowUpRight size={16} className="text-[#C9A45C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW ERRAH PROTECTS */}
      <section className="border-t border-white/10 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16">
          <Reveal><h2 className="serif-head text-3xl lg:text-5xl">How ERRAH protects</h2></Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-3.5">
            {TYPES.map((t, i) => (
              <Reveal key={t.tag} delay={i * 0.08}>
                <div className="hair-border bg-white/[0.02] p-7 h-full">
                  <t.icon className="text-[#C9A45C]" size={26} />
                  <p className="errah-label gold-text mt-5">{t.tag}</p>
                  <h3 className="font-display text-xl mt-1">{t.title}</h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
